/**
 * User Detail Page
 * =================
 * صفحة تفاصيل المستخدم - بيانات حقيقية من قاعدة البيانات
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../../lib/ui/Card';
import { Button } from '../../../lib/ui/Button';
import { Modal } from '../../../lib/ui/Modal';
import { Input } from '../../../lib/ui/Input';
import { usersService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import './UserDetailPage.css';

const Icons = {
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  key: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  phone: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  building: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/></svg>,
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
};

const UserDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadUser();
  }, [id]);

  const loadUser = async () => {
    setLoading(true);
    try {
      const data = await usersService.getById(id);
      if (data) {
        setUser({
          ...data,
          source: 'database'
        });
        setEditData({
          full_name: data.full_name,
          email: data.email,
          phone: data.phone,
        });
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      toast.error('فشل في تحميل بيانات المستخدم');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      const newStatus = user.status === 'active' ? 'suspended' : 'active';
      await usersService.update(id, { status: newStatus });
      toast.success(newStatus === 'active' ? 'تم تفعيل المستخدم' : 'تم تعليق المستخدم');
      loadUser();
    } catch (error) {
      toast.error('فشل تحديث حالة المستخدم');
    }
  };

  const handleUpdateUser = async () => {
    setSaving(true);
    try {
      await usersService.update(id, editData);
      toast.success('تم تحديث بيانات المستخدم');
      setShowEditModal(false);
      loadUser();
    } catch (error) {
      toast.error('فشل تحديث بيانات المستخدم');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    setSaving(true);
    try {
      await usersService.update(id, { password: newPassword });
      toast.success('تم تغيير كلمة المرور بنجاح');
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (error) {
      toast.error('فشل تغيير كلمة المرور');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    setSaving(true);
    try {
      await usersService.delete(id);
      toast.success('تم حذف المستخدم');
      navigate('/admin/users');
    } catch (error) {
      toast.error('فشل حذف المستخدم');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="watrly-admin-loading"><div className="watrly-admin-loading__spinner" /></div>;
  }

  if (!user) {
    return (
      <div className="watrly-user-detail">
        <div className="watrly-user-detail__empty">
          <p>لم يتم العثور على المستخدم</p>
          <Button onClick={() => navigate('/admin/users')}>العودة للقائمة</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="watrly-user-detail">
      {/* Header */}
      <div className="watrly-user-detail__header">
        <div className="watrly-user-detail__header-info">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')}>
            {Icons.back}
          </Button>
          <div className="watrly-user-detail__avatar">
            {user.full_name?.charAt(0) || 'م'}
          </div>
          <div className="watrly-user-detail__title-group">
            <h1>{user.full_name} <small style={{fontSize: '0.6em', color: '#888'}}>- from database</small></h1>
            <div className="watrly-user-detail__meta">
              <span className={`watrly-user-detail__status watrly-user-detail__status--${user.status}`}>
                <span className="watrly-user-detail__status-dot" />
                {user.status === 'active' ? 'نشط' : 'معلق'}
              </span>
              {user.is_superuser && (
                <span className="watrly-user-detail__badge watrly-user-detail__badge--admin">
                  {Icons.shield} مدير النظام
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="watrly-user-detail__header-actions">
          <Button variant="ghost" onClick={() => setShowEditModal(true)}>
            {Icons.edit}
            تعديل
          </Button>
          <Button variant="ghost" onClick={() => setShowPasswordModal(true)}>
            {Icons.key}
            تغيير كلمة المرور
          </Button>
          <Button 
            variant={user.status === 'active' ? 'warning' : 'success'} 
            onClick={handleToggleStatus}
          >
            {user.status === 'active' ? Icons.x : Icons.check}
            {user.status === 'active' ? 'تعليق' : 'تفعيل'}
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
            {Icons.trash}
            حذف
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="watrly-user-detail__grid">
        <Card padding="md" className="watrly-user-detail__card">
          <h3 className="watrly-user-detail__card-title">معلومات الاتصال</h3>
          <div className="watrly-user-detail__info-list">
            <div className="watrly-user-detail__info-item">
              <span className="watrly-user-detail__info-icon">{Icons.mail}</span>
              <div className="watrly-user-detail__info-content">
                <span className="watrly-user-detail__info-label">البريد الإلكتروني</span>
                <span className="watrly-user-detail__info-value">{user.email}</span>
              </div>
            </div>
            <div className="watrly-user-detail__info-item">
              <span className="watrly-user-detail__info-icon">{Icons.phone}</span>
              <div className="watrly-user-detail__info-content">
                <span className="watrly-user-detail__info-label">رقم الهاتف</span>
                <span className="watrly-user-detail__info-value">{user.phone || '-'}</span>
              </div>
            </div>
          </div>
        </Card>

        <Card padding="md" className="watrly-user-detail__card">
          <h3 className="watrly-user-detail__card-title">معلومات الحساب</h3>
          <div className="watrly-user-detail__info-list">
            <div className="watrly-user-detail__info-item">
              <span className="watrly-user-detail__info-icon">{Icons.building}</span>
              <div className="watrly-user-detail__info-content">
                <span className="watrly-user-detail__info-label">الشركة</span>
                <span className="watrly-user-detail__info-value">{user.tenant?.name || user.tenant_name || '-'}</span>
              </div>
            </div>
            <div className="watrly-user-detail__info-item">
              <span className="watrly-user-detail__info-icon">{Icons.shield}</span>
              <div className="watrly-user-detail__info-content">
                <span className="watrly-user-detail__info-label">الدور</span>
                <span className="watrly-user-detail__info-value">
                  {user.is_superuser ? 'مدير النظام' : (user.role_name || 'مستخدم عادي')}
                </span>
              </div>
            </div>
          </div>
        </Card>

        <Card padding="md" className="watrly-user-detail__card">
          <h3 className="watrly-user-detail__card-title">معلومات الجلسة</h3>
          <div className="watrly-user-detail__info-list">
            <div className="watrly-user-detail__info-item">
              <span className="watrly-user-detail__info-icon">{Icons.clock}</span>
              <div className="watrly-user-detail__info-content">
                <span className="watrly-user-detail__info-label">آخر تسجيل دخول</span>
                <span className="watrly-user-detail__info-value">{formatDate(user.last_login_at)}</span>
              </div>
            </div>
            <div className="watrly-user-detail__info-item">
              <span className="watrly-user-detail__info-icon">{Icons.clock}</span>
              <div className="watrly-user-detail__info-content">
                <span className="watrly-user-detail__info-label">تاريخ الإنشاء</span>
                <span className="watrly-user-detail__info-value">{formatDate(user.created_at)}</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="تعديل بيانات المستخدم"
        size="md"
        footer={
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setShowEditModal(false)}>إلغاء</Button>
            <Button variant="primary" onClick={handleUpdateUser} loading={saving}>حفظ</Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input
            label="الاسم الكامل"
            value={editData.full_name || ''}
            onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
          />
          <Input
            label="البريد الإلكتروني"
            type="email"
            value={editData.email || ''}
            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
          />
          <Input
            label="رقم الهاتف"
            value={editData.phone || ''}
            onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
          />
        </div>
      </Modal>

      {/* Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => { setShowPasswordModal(false); setNewPassword(''); }}
        title="تغيير كلمة المرور"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => { setShowPasswordModal(false); setNewPassword(''); }}>إلغاء</Button>
            <Button variant="primary" onClick={handleChangePassword} loading={saving}>تغيير</Button>
          </div>
        }
      >
        <Input
          label="كلمة المرور الجديدة"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="أدخل كلمة المرور الجديدة"
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="تأكيد الحذف"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>إلغاء</Button>
            <Button variant="danger" onClick={handleDeleteUser} loading={saving}>حذف</Button>
          </div>
        }
      >
        <div style={{ textAlign: 'center', padding: '1rem' }}>
          <p>هل أنت متأكد من حذف المستخدم <strong>{user.full_name}</strong>؟</p>
          <p style={{ fontSize: '0.875rem', color: '#EF4444', marginTop: '0.5rem' }}>
            هذا الإجراء لا يمكن التراجع عنه
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default UserDetailPage;
